/**
 * AdminPreviewContext
 *
 * Lightweight context that lets admins preview the Team Lead or Agent experience
 * when viewing another team. Only wraps the main content area — sidebar, auth
 * providers, and data providers are unaffected and won't re-render on toggle.
 *
 * Components that read role-based state deep in the tree (e.g. CallEvaluator
 * sub-components) can call useAdminPreview() instead of hard-coding useAuth()
 * role values, making the preview accurate without prop-drilling.
 *
 * Usage:
 *   const { effectiveUserRole, effectiveIsAdmin, effectiveIsTeamLead, isPreviewActive } = useAdminPreview();
 *
 * When NOT in preview mode (normal users, or admin on their own team) all
 * values mirror the real auth state so there is zero behaviour change.
 */

import React, { createContext, useContext } from 'react';
import { useAuth } from './AuthContext';

const AdminPreviewContext = createContext(null);

/**
 * Hook for consuming the admin preview state.
 * Falls back gracefully to real auth state when used outside the provider.
 */
export const useAdminPreview = () => {
  const ctx = useContext(AdminPreviewContext);
  const auth = useAuth();

  if (!ctx) {
    // Outside provider — return real auth values unchanged
    return {
      effectiveUserRole: auth.userRole,
      effectiveIsAdmin: auth.isAdmin,
      effectiveIsTeamLead: auth.isTeamLead,
      isPreviewActive: false,
    };
  }

  return ctx;
};

/**
 * AdminPreviewProvider
 *
 * Props:
 *   isViewingAsAgent    {boolean}  — true when admin has toggled to "Agent" view
 *   isViewingAsTeamLead {boolean}  — true when admin has toggled to "Team Lead" view
 *
 * Wrap only the main content area with this provider, not the entire app.
 */
export const AdminPreviewProvider = ({ children, isViewingAsAgent, isViewingAsTeamLead }) => {
  const { userRole, isAdmin, isTeamLead } = useAuth();

  // Override effective role based on which preview mode is active
  const effectiveUserRole   = isViewingAsAgent ? 'basic'
                            : isViewingAsTeamLead ? 'team_lead'
                            : userRole;
  const effectiveIsAdmin    = (isViewingAsAgent || isViewingAsTeamLead) ? false : isAdmin;
  const effectiveIsTeamLead = isViewingAsAgent  ? false
                            : isViewingAsTeamLead ? true
                            : isTeamLead;
  const isPreviewActive     = isViewingAsAgent || isViewingAsTeamLead;

  return (
    <AdminPreviewContext.Provider value={{
      effectiveUserRole,
      effectiveIsAdmin,
      effectiveIsTeamLead,
      isPreviewActive,
    }}>
      {children}
    </AdminPreviewContext.Provider>
  );
};
